import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Package, Loader2, Search, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { TABS, FIELDS, TABLE_COLS, COL_LABELS, STATUT_COLORS, STATUT_LABELS } from "./outilsConstants";

export default function PersonalEquipmentsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [groups, setGroups] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/personal/my-equipements");
      setGroups(r.data.groups || {});
      setTotal(r.data.total || 0);
      const firstCat = Object.keys(r.data.groups || {})[0];
      if (!activeCat && firstCat) setActiveCat(firstCat);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const cats = TABS.filter(t => groups[t.key]?.length > 0);
  const currentItems = activeCat ? (groups[activeCat] || []) : [];
  const filtered = currentItems.filter(it => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(it).some(v => typeof v === "string" && v.toLowerCase().includes(s));
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="personal-equipments-page">
      <header className="mb-6">
        <h1 className="text-3xl font-black text-white flex items-center gap-2">
          <Package className="w-7 h-7 text-[#f7c935]" /> Mon Equipement
        </h1>
        <p className="text-sm text-slate-500 mt-1">Vue des equipements, cartes et acces qui vous sont attribues dans le module Outils.</p>
      </header>

      {/* Summary */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-4" data-testid="equipments-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Total attribue</div>
            <div className="text-3xl font-black text-[#f7c935] mt-0.5">{total}</div>
          </div>
          <div className="text-right text-[11px] text-slate-500 max-w-xs">
            Ces elements sont rattaches au module <span className="text-white font-semibold">Outils &gt; Gestion de l'activite</span>. Ils sont mis a jour automatiquement quand un administrateur vous les attribue.
          </div>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" />
      ) : cats.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-10 text-center" data-testid="no-equipments">
          <Package className="w-10 h-10 mx-auto text-slate-600 mb-3" />
          <p className="text-sm text-slate-500">Aucun equipement attribue pour le moment.</p>
          <p className="text-xs text-slate-600 mt-1">Les equipements attribues par votre admin apparaitront ici.</p>
        </div>
      ) : (
        <>
          {/* Category tabs */}
          <div className="flex gap-1 bg-[#14101c] border border-white/5 rounded-xl p-1 mb-4 overflow-x-auto" data-testid="equipments-cat-tabs">
            {cats.map(c => {
              const Icon = c.icon;
              const count = groups[c.key]?.length || 0;
              const active = activeCat === c.key;
              return (
                <button key={c.key} onClick={() => setActiveCat(c.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#f7c935]/15 text-[#f7c935]" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
                  data-testid={`equipments-tab-${c.key}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {c.label}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${active ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/10"}`}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-3 max-w-md">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
              className="bg-[#14101c] border-white/10 text-white pl-9 h-9 text-sm" data-testid="equipments-search" />
          </div>

          {/* Items grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((it, i) => (
              <EquipmentCard key={it.id || i} item={it} category={activeCat} />
            ))}
          </div>
          {filtered.length === 0 && search && (
            <p className="text-center text-sm text-slate-500 py-8">Aucun resultat pour "{search}"</p>
          )}
        </>
      )}
    </div>
  );
}

function EquipmentCard({ item, category }) {
  const fields = FIELDS[category] || [];
  const cols = TABLE_COLS[category] || [];
  const statut = item.statut || "actif";
  const statutCls = STATUT_COLORS[statut] || STATUT_COLORS.actif;
  const statutLabel = STATUT_LABELS[statut] || statut;

  // Headline: first 'name-like' field
  const headlineField = fields.find(f => ["marque", "type_equipement", "type_paiement", "fournisseur", "nom"].includes(f.key));
  const headline = headlineField ? item[headlineField.key] : "";
  const subField = fields.find(f => f.key === "modele" || f.key === "numero_carte" || f.key === "numero_badge" || f.key === "numero");
  const sub = subField ? item[subField.key] : "";

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`my-equipment-${item.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white truncate">{headline || "Equipement"}</div>
          {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${statutCls}`}>{statutLabel}</span>
      </div>
      <div className="space-y-1 text-[11px]">
        {cols.map(col => {
          if (["marque", "type_equipement", "type_paiement", "fournisseur", "nom", "modele", "numero_carte", "numero_badge", "numero", "statut"].includes(col)) return null;
          const val = item[col];
          if (!val) return null;
          return (
            <div key={col} className="flex justify-between gap-2">
              <span className="text-slate-500">{COL_LABELS[col] || col}</span>
              <span className="text-white truncate max-w-[60%] text-right">{String(val)}</span>
            </div>
          );
        })}
        {item.date_expiration && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Expiration</span>
            <span className="text-white">{item.date_expiration}</span>
          </div>
        )}
        {item.garantie_fin && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Garantie jusqu'au</span>
            <span className="text-white">{item.garantie_fin}</span>
          </div>
        )}
        {item.notes && (
          <div className="text-slate-400 italic mt-2 pt-2 border-t border-white/5 text-[10px] line-clamp-2">{item.notes}</div>
        )}
      </div>
    </div>
  );
}
